import {
  Button as MantineButton,
  ButtonProps as MantineButtonProps,
  PolymorphicComponentProps,
  createPolymorphicComponent,
} from '@mantine/core';
import { ComponentPropsWithoutRef, ElementType, forwardRef } from 'react';

import styles from './Button.module.css';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'tertiaryBg'
  | 'quaternary'
  | 'positive'
  | 'negative'
  | 'ai';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface BaseButtonProps extends Omit<
  MantineButtonProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size'
> {
  /** Visual variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Whether to apply FullStory PII/PHI masking (fs-mask class) */
  isMasked?: boolean;
  /** Disable hover effects */
  isHoverDisabled?: boolean;
  isActive?: boolean;
}

export type ButtonProps = BaseButtonProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof BaseButtonProps | 'style'>;

/**
 * Map component sizes to icon sizes for consistent icon scaling.
 * These match Figma specifications.
 */
const ICON_SIZE: Record<ButtonSize, number> = {
  xs: 16,
  sm: 20,
  md: 20,
  lg: 20,
};

/**
 * Map variants to loader colors.
 * Primary/Negative use white, Secondary/Tertiary use link color.
 */
const LOADER_COLOR: Record<ButtonVariant, string> = {
  primary: 'var(--color-text-on-primary)',
  secondary: 'var(--color-text-link-primary)',
  tertiary: 'var(--color-text-link-primary)',
  tertiaryBg: 'var(--color-text-link-primary)',
  quaternary: 'var(--color-text-default-primary)',
  negative: 'var(--color-text-on-primary)',
  positive: 'var(--color-text-success-primary)',
  ai: 'var(--color-text-on-primary)',
};

const _Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isMasked,
      isHoverDisabled = false,
      className,
      children,
      leftSection,
      rightSection,
      isActive,
      ...props
    },
    ref
  ) => {
    const isIconOnly =
      (children === null || children === undefined || children === '') &&
      !!(leftSection || rightSection);
    const iconSize =
      variant === 'quaternary' && size === 'sm' ? 16 : ICON_SIZE[size];

    const rootClassName = [styles.root, isMasked ? 'fs-mask' : null, className]
      .filter(Boolean)
      .join(' ');

    return (
      <MantineButton
        ref={ref}
        type="button"
        unstyled
        classNames={{
          root: rootClassName,
          inner: styles.inner,
          section: styles.section,
          label: styles.label,
          loader: styles.loader,
        }}
        data-variant={variant}
        data-button-size={size}
        data-icon-only={isIconOnly || undefined}
        data-disable-hover={isHoverDisabled || undefined}
        data-active={isActive}
        leftSection={leftSection}
        rightSection={rightSection}
        loaderProps={{ size: iconSize, color: LOADER_COLOR[variant] }}
        {...props}
      >
        {children}
      </MantineButton>
    );
  }
);

_Button.displayName = 'Button';

type ButtonComponent = <C extends ElementType = 'button'>(
  props: Omit<PolymorphicComponentProps<C, ButtonProps>, 'style'>
) => React.ReactElement | null;

export const Button = createPolymorphicComponent<'button', ButtonProps>(
  _Button
) as unknown as ButtonComponent;
